import React, { useState } from 'react';

interface PreviewItem {
  id: string;
  type: 'table' | 'image' | 'chart' | 'code';
  title: string;
  content: any;
  timestamp: number;
}

interface PreviewWindowProps {
  items: PreviewItem[];
  selectedItem: string | null;
  onItemSelect: (id: string) => void;
}

const PreviewWindow: React.FC<PreviewWindowProps> = ({ 
  items, 
  selectedItem, 
  onItemSelect 
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const renderPreviewContent = (item: PreviewItem) => {
    switch (item.type) {
      case 'table':
        return (
          <div className="overflow-auto">
            <div className="text-sm text-ie-slate p-4">
              Table preview will be implemented here
            </div>
          </div>
        );
      case 'image':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-ie-slate">
              Image preview will be implemented here
            </div>
          </div>
        );
      case 'chart':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-ie-slate">
              Chart preview will be implemented here
            </div>
          </div>
        );
      case 'code':
        return (
          <div className="p-4">
            <pre className="text-sm bg-ie-mist p-3 rounded-lg overflow-auto">
              <code>{item.content}</code>
            </pre>
          </div>
        );
      default:
        return null;
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'table':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
          </svg>
        );
      case 'image':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
        );
      case 'chart':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
        );
      case 'code':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`h-full bg-white border-l border-ie-rule transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-80'
    }`}>
      {/* Header */}
      <div className="p-4 border-b border-ie-rule">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 text-ie-slate hover:text-ie-navy hover:bg-ie-mist rounded-lg transition-colors"
          >
            <svg 
              className={`w-5 h-5 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
          {!isCollapsed && (
            <h2 className="text-lg font-semibold text-ie-navy">Preview</h2>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {items.length === 0 && !isCollapsed && (
          <div className="text-center py-8 px-4">
            <div className="w-12 h-12 bg-ie-mist rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-ie-slate" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-sm text-ie-slate">No preview items</p>
            <p className="text-xs text-ie-slate mt-1">Tables, images, and charts will appear here</p>
          </div>
        )}

        {!isCollapsed && items.length > 0 && (
          <div className="h-full flex flex-col">
            {/* Items List */}
            <div className="p-2 border-b border-ie-rule">
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => onItemSelect(item.id)}
                    className={`w-full p-2 rounded-lg text-left transition-all duration-200 ${
                      selectedItem === item.id
                        ? 'bg-ie-mist border border-ie-sky'
                        : 'hover:bg-ie-pale border border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`${
                        selectedItem === item.id ? 'text-ie-steel' : 'text-ie-slate'
                      }`}>
                        {getIcon(item.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          selectedItem === item.id ? 'text-ie-navy' : 'text-ie-navy'
                        }`}>
                          {item.title}
                        </p>
                        <p className={`text-xs ${
                          selectedItem === item.id ? 'text-ie-steel' : 'text-ie-slate'
                        }`}>
                          {item.type}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-hidden">
              {selectedItem && items.find(item => item.id === selectedItem) ? (
                <div className="h-full flex flex-col">
                  <div className="p-3 border-b border-ie-rule">
                    <h3 className="font-medium text-ie-navy">
                      {items.find(item => item.id === selectedItem)?.title}
                    </h3>
                  </div>
                  <div className="flex-1 overflow-auto">
                    {renderPreviewContent(items.find(item => item.id === selectedItem)!)}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-ie-slate">Select an item to preview</p>
                </div>
              )}
            </div>
          </div>
        )}

        {isCollapsed && items.length > 0 && (
          <div className="p-2 space-y-2">
            {items.slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => onItemSelect(item.id)}
                className={`w-full p-2 rounded-lg transition-all duration-200 ${
                  selectedItem === item.id
                    ? 'bg-ie-mist border border-ie-sky'
                    : 'hover:bg-ie-pale border border-transparent'
                }`}
                title={item.title}
              >
                <div className={`flex items-center justify-center ${
                  selectedItem === item.id ? 'text-ie-steel' : 'text-ie-slate'
                }`}>
                  {getIcon(item.type)}
                </div>
              </button>
            ))}
            {items.length > 5 && (
              <div className="text-center py-1">
                <span className="text-xs text-ie-slate">+{items.length - 5} more</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewWindow;